import type { Service } from '~publish/pages/Community/graphqlEnums'

import type {
  DateRange,
  FilterStatus,
  SortOption,
} from '../../CommunityFilters/types'
import { useCommentsTracking } from '../tracking/useCommentsTracking'

type Channel = {
  id: string
  service: Service
}

type FilterActions = {
  setStatus: (status: FilterStatus) => void
  setSortOption: (option: SortOption) => void
  setPublishedAt: (dateRange: DateRange) => void
}

type UseFilterHandlersArgs = {
  channels: Channel[]
  filterActions: FilterActions
}

type UseFilterHandlersReturn = {
  handleStatusChange: (newStatus: FilterStatus) => void
  handleDateRangeChange: (newDateRange: DateRange) => void
  handleSortOptionChange: (newSortOption: SortOption) => void
}

export function useFilterHandlers({
  channels,
  filterActions,
}: UseFilterHandlersArgs): UseFilterHandlersReturn {
  const { setStatus, setSortOption, setPublishedAt } = filterActions
  const { trackCommentsFiltered, trackCommentsSorted } = useCommentsTracking()

  const handleStatusChange = (newStatus: FilterStatus): void => {
    setStatus(newStatus)
    trackCommentsFiltered({
      channels: channels.map((channel) => ({
        id: channel.id,
        service: channel.service,
      })),
      filterType: 'status',
      filterValue: String(newStatus),
    })
  }

  const handleDateRangeChange = (newDateRange: DateRange): void => {
    setPublishedAt(newDateRange)
    trackCommentsFiltered({
      channels: channels.map((channel) => ({
        id: channel.id,
        service: channel.service,
      })),
      filterType: 'date',
      filterValue: JSON.stringify(newDateRange),
    })
  }

  const handleSortOptionChange = (newSortOption: SortOption): void => {
    setSortOption(newSortOption)
    trackCommentsSorted({
      channels: channels.map((channel) => ({
        id: channel.id,
        service: channel.service,
      })),
      sortType: 'sort',
      sortValue: String(newSortOption),
    })
  }

  return {
    handleStatusChange,
    handleDateRangeChange,
    handleSortOptionChange,
  }
}
